import { TenantsColumns } from "./columns";
import { z } from "zod";
import { TermMetaSchema } from "../schemas";

const min = 3;
const max = 20;

const DisplayNameFieldSchema = z.object({
  name: z.string().min(min).max(max),
  plural: z.string().min(min).max(max),
});

export const DisplayNamesSchema = z
  .object({
    projects: DisplayNameFieldSchema.default({ name: "Project", plural: "Projects" }).describe(
      "Main work units or jobs",
    ),
    shifts: DisplayNameFieldSchema.default({ name: "Shift", plural: "Shifts" }).describe("Scheduled work periods"),
    time_cards: DisplayNameFieldSchema.default({ name: "Time Card", plural: "Time Cards" }).describe(
      "Time tracking records",
    ),
    chat: DisplayNameFieldSchema.default({ name: "Chat", plural: "Chats" }).describe("Messaging system"),
    courses: DisplayNameFieldSchema.default({ name: "Course", plural: "Courses" }).describe(
      "Training and certification courses",
    ),
    expenses: DisplayNameFieldSchema.default({ name: "Expense", plural: "Expenses" }).describe("Expense reports"),
    users: DisplayNameFieldSchema.default({ name: "User", plural: "Users" }).describe("System users and workers"),
    roles: DisplayNameFieldSchema.default({ name: "Role", plural: "Roles" }).describe("Permission groups"),
    clients: DisplayNameFieldSchema.default({ name: "Client", plural: "Clients" }).describe("Customer organizations"),
    contacts: DisplayNameFieldSchema.default({ name: "Contact", plural: "Contacts" }).describe(
      "Client representatives",
    ),
    incidents: DisplayNameFieldSchema.default({ name: "Incident", plural: "Incidents" }).describe(
      "Safety reports and events",
    ),
    investigations: DisplayNameFieldSchema.default({ name: "Investigation", plural: "Investigations" }).describe(
      "Incident investigations",
    ),
    payroll: DisplayNameFieldSchema.default({ name: "Payroll Entry", plural: "Payroll" }).describe(
      "Employee compensation records",
    ),
    invoices: DisplayNameFieldSchema.default({ name: "Invoice", plural: "Invoices" }).describe("Bills sent to clients"),
    estimates: DisplayNameFieldSchema.default({ name: "Estimate", plural: "Estimates" }).describe("Project quotes"),
    documents: DisplayNameFieldSchema.default({ name: "Document", plural: "Documents" }).describe(
      "Files and attachments",
    ),
    applications: DisplayNameFieldSchema.default({ name: "Application", plural: "Applications" }).describe(
      "Job applications",
    ),
  })
  .default({
    projects: { name: "Project", plural: "Projects" },
    shifts: { name: "Shift", plural: "Shifts" },
    time_cards: { name: "Time Card", plural: "Time Cards" },
    chat: { name: "Chat", plural: "Chats" },
    users: { name: "User", plural: "Users" },
    roles: { name: "Role", plural: "Roles" },
    clients: { name: "Client", plural: "Clients" },
    contacts: { name: "Contact", plural: "Contacts" },
    incidents: { name: "Incident", plural: "Incidents" },
    investigations: { name: "Investigation", plural: "Investigations" },
    payroll: { name: "Payroll Entry", plural: "Payroll" },
    invoices: { name: "Invoice", plural: "Invoices" },
    estimates: { name: "Estimate", plural: "Estimates" },
    documents: { name: "Document", plural: "Documents" },
    applications: { name: "Application", plural: "Applications" },
  })
  .describe("Display names for app screens with singular and plural forms.");

// Helper to extract default values from DisplayNamesSchema
export const getDisplayNameDefaults = (): z.infer<typeof DisplayNamesSchema> => {
  const schemaShape = (DisplayNamesSchema as any)._def.innerType.shape;
  const defaults: any = {};

  Object.keys(schemaShape).forEach((key) => {
    const fieldSchema = schemaShape[key];
    // Access the default value from the field's _def
    const defaultValue = fieldSchema._def?.defaultValue?.() || { name: "", plural: "" };
    defaults[key] = defaultValue;
  });

  return defaults;
};

export const PasswordRequirementsSchema = z.object({
  min_length: z.number().min(6).max(32).default(8),
  require_uppercase: z.boolean().default(true),
  require_lowercase: z.boolean().default(true),
  require_number: z.boolean().default(true),
  require_special: z.boolean().default(true),
});

export const PasswordPolicySchema = z.object({
  update_days: z.number().min(30).max(360).optional(),
  requirements: PasswordRequirementsSchema.optional(),
});

// Default password requirements for use in UI and validation
export const DEFAULT_PASSWORD_REQUIREMENTS: z.infer<typeof PasswordRequirementsSchema> = {
  min_length: 8,
  require_uppercase: true,
  require_lowercase: true,
  require_number: true,
  require_special: true,
};

export const TenantAddressSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().max(2).min(2),
  zip: z.string().regex(/^\d{5}/, "Zip must be #####"),
  country: z.string().optional(),
});

export type TenantAddressType = z.infer<typeof TenantAddressSchema>;

const TenantInfoSchema = z.object({
  contact: z.any().optional(),
  display_names: DisplayNamesSchema.optional(),
  starting_counts: z.object({ events: z.number(), invoices: z.number(), estimates: z.number() }).partial().optional(),
});

const TenantLabelsSchema = z.object({
  markets: z.record(z.object({ display_name: z.string(), fips_code: z.number() })).optional(),
  locations: z.array(z.any()).optional(),
  positions: z.record(z.any()).optional(),
  dress_codes: z.record(z.object({ description: z.string() })).optional(),
  billing_items: z.record(z.any()).optional(),
  incident_labels: z.array(z.object({ name: z.string(), description: z.string() })).optional(),
  requirement_labels: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        type: z.enum(["ability", "restriction"]),
        description: z.string(),
      }),
    )
    .optional(),
});

export const TenantMetaSchema = z.object({
  terms: TermMetaSchema.optional(),
  password_policy: PasswordPolicySchema.optional(),
  max_failed_sign_in_attempts: z.number().min(1).max(10).optional(),
  addresses: z.array(TenantAddressSchema).optional(),
  info: TenantInfoSchema.optional(),
  labels: TenantLabelsSchema.optional(),
});

export type TenantMetaType = z.infer<typeof TenantMetaSchema>;

export type DisplayNamesType = z.infer<typeof DisplayNamesSchema>;

export type TenantType = Omit<TenantsColumns, "meta_data" | "display_names"> & {
  display_names: DisplayNamesType;
  meta_data: TenantMetaType;
};
